import TextInput from '@components/dataEntry/Input/TextInput';
import { sendPasswordResetEmail, sendPasswordResetEmailWithAlert } from '@store/firebase/user/registerUser';
import { validateEmailInput } from '@utils/loginValidate.function';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Button from '@components/dataEntry/button/Button';

const Wrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 32px;
`;

// 🔧 에러 메시지 표시 영역
const ErrorSec = styled.p`
  width: 100%;
  background:rgb(199, 56, 68);
  color: white;
  border-radius: var(--cardBorderRadius);
  padding: 12px 16px;
  text-align: center;
  font-size: 14px;
  margin: 8px 0;
`;

// 🔧 성공 메시지 표시 영역
const SuccessSec = styled.p`
  width: 100%;
  background: #2ed573;
  color: white;
  border-radius: var(--cardBorderRadius);
  padding: 12px 16px;
  text-align: center;
  font-size: 14px;
  margin: 8px 0;
`;

type Props = {
  close?: () => void;
};

function ResetPassword({ close }: Props) {
  const history = useNavigate();
  const [value, setValue] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [btnDisabled, setBtnDistabled] = useState(true);
  const [pending, setPending] = useState<boolean>(false);
  const [confirmSend, setConfirmSend] = useState(false);

  // 🔧 에러 및 성공 상태 관리
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [sendAttempts, setSendAttempts] = useState<number>(0);

  const changeEmail = useCallback((e: any) => {
    const value: string = e.target.value;
    const isValid: boolean = validateEmailInput(value);

    setValue(value);
    setIsEmailValid(!isValid);

    // 🔧 이메일 변경 시 에러/성공 메시지 초기화
    setErrorMessage('');
    setSuccessMessage('');
  }, []);

  useEffect(() => {
    setBtnDistabled(!(!isEmailValid && value));
  }, [value, isEmailValid]);

  const handleSubmit = useCallback(async () => {
    setPending(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // 먼저 Alert 버전 실행 (브라우저 팝업)
      const alertResult = await sendPasswordResetEmailWithAlert(value);

      // 그 다음 Toast 버전 실행 (화면 상단 알림) - 팝업과 별도로
      const toastResult = await sendPasswordResetEmail(value);


      // 둘 중 하나라도 성공하면 성공으로 처리
      const result = alertResult?.success || toastResult?.success ? alertResult || toastResult : alertResult;

      if (result && result.success) {
        setSuccessMessage(result.message || '비밀번호 재설정 이메일이 전송되었습니다.');
        setConfirmSend(true);
        setSendAttempts(prev => prev + 1);
      } else {
        // 실패한 경우
        const errorMsg = result?.message || '이메일 전송에 실패했습니다.';
        setErrorMessage(errorMsg);
      }
    } catch (error: any) {

      // 🔧 에러 타입별 메시지 처리
      let errorMsg = '알 수 없는 오류가 발생했습니다.';

      if (error.message) {
        errorMsg = error.message;
      } else if (typeof error === 'string') {
        errorMsg = error;
      }

      setErrorMessage(errorMsg);
    } finally {
      setPending(false);
    }
  }, [value]);

  // 🔧 재시도 함수
  const handleRetry = useCallback(() => {
    setErrorMessage('');
    setSuccessMessage('');
    setConfirmSend(false);
    setSendAttempts(0);
  }, []);

  return (
    <Wrap>
      {!confirmSend && (
        <>
          <h4>비밀번호 재설정</h4>
          <p>
            회원가입 하셨던 이메일을 통해 <br />
            비밀번호를 재설정 할 수 있습니다.
          </p>

          {/* 🔧 에러 메시지 표시 */}
          {errorMessage && (
            <ErrorSec>
              <strong>⚠️ 오류 발생</strong><br />
              {errorMessage}
            </ErrorSec>
          )}

          {/* 🔧 성공 메시지 표시 */}
          {successMessage && !confirmSend && (
            <SuccessSec>
              <strong>✅ 전송 완료</strong><br />
              {successMessage}
            </SuccessSec>
          )}

          <TextInput
            value={value}
            onChange={changeEmail}
            name="reset-email"
            block
            placeholder="이메일 example@example.com"
            validType="email"
            isValid={isEmailValid}
            validMessage="이메일 형식을 올바르게 입력해주세요."
          />
          <Button
            onClick={handleSubmit}
            pending={pending}
            disabled={btnDisabled}
          >
            {sendAttempts > 0 ? '비밀번호 재설정 이메일 다시 보내기' : '비밀번호 재설정 이메일 보내기'}
          </Button>

          <Button
            shape="ghost"
            onClick={() => {
              if (close) close();
              history('/');
            }}
          >
            취소
          </Button>
        </>
      )}

      {confirmSend && (
        <>
          <h4>
            비밀번호 재설정 이메일 <br /> 발송 완료
          </h4>

          {/* 🔧 성공 메시지 표시 */}
          {successMessage && (
            <SuccessSec>
              <strong>✅ 이메일 전송 성공</strong><br />
              {successMessage}
            </SuccessSec>
          )}

          {/* 🔧 에러 메시지 표시 (재전송 시 발생할 수 있음) */}
          {errorMessage && (
            <ErrorSec>
              <strong>⚠️ 재전송 실패</strong><br />
              {errorMessage}
            </ErrorSec>
          )}

          <p>
            아래 이메일 주소에서 <br /> no-reploy에서 전송된 메일을 확인해주세요.
            <br />
            <strong>이메일이 도착하지 않으면 스팸메일함을 살펴주세요.</strong>
            <br /><br />
            {/* 🔧 전송 횟수 표시 */}
            {sendAttempts > 1 && (
              <span style={{ color: 'var(--subTextColor)', fontSize: '12px' }}>
                (총 {sendAttempts}번 전송됨)
              </span>
            )}
          </p>


          {/* 🔧 이메일 수정 가능하도록 입력 필드 유지 */}
          <TextInput
            value={value}
            onChange={changeEmail}
            name="reset-email"
            block
            placeholder="이메일 example@example.com"
            validType="email"
            isValid={isEmailValid}
            validMessage="이메일 형식을 올바르게 입력해주세요."
          />

          <Button
            onClick={handleSubmit}
            pending={pending}
            disabled={btnDisabled}
          >
            재설정 메일 다시 보내기 ({sendAttempts + 1}번째)
          </Button>

          {/* 🔧 처음부터 다시 시작하기 버튼 */}
          <Button
            shape="ghost"
            onClick={handleRetry}
          >
            다른 이메일로 다시 시도
          </Button>

          <Button
            onClick={() => {
              if (close) close();
              history('/');
            }}
          >
            완료
          </Button>
        </>
      )}
    </Wrap>
  );
}

export default ResetPassword;
